import type { ImageLoader, ImageLoaderProps } from 'next/image'
import config from '../common/config'

// Hosts fronted by Akamai Image & Video Manager, which resizes + format-negotiates at the edge via
// the `imwidth` param — so Next's on-pod `/_next/image` optimizer can be skipped.
const IVM_HOSTS = new Set([
    'images.traum-ferienwohnungen.de',
    'img2.traum-ferienwohnungen.de',
    'static2.traum-ferienwohnungen.de',
    'media.traum-ferienwohnungen.de',
])

// App `/assets` are served from the www Akamai property, whose file-extension rule also runs IVM.
const isLocalAsset = (src: string): boolean => src.startsWith('/assets/')

// True when `src` can be optimized by Akamai IVM instead of the on-pod optimizer.
export const isAkamaiOptimizable = (src: string): boolean => {
    if (isLocalAsset(src)) {
        return true
    }
    try {
        return IVM_HOSTS.has(new URL(src).hostname)
    } catch {
        return false
    }
}

// Pins the image to a fixed `imwidth` (falling back to next/image's computed width) and returns a
// direct edge URL, bypassing the on-pod optimizer. next/image calls this per candidate width, so
// keep the component's `sizes`/`width`/`height` consistent with one fixed size.
export const createAkamaiImageLoader =
    (imwidth?: number): ImageLoader =>
    ({ src, width }: ImageLoaderProps): string => {
        const targetWidth = String(imwidth ?? width)
        if (isLocalAsset(src)) {
            const base = `${config.assetPrefix}${src}`
            const separator = base.includes('?') ? '&' : '?'
            return `${base}${separator}imwidth=${targetWidth}`
        }
        const url = new URL(src)
        url.searchParams.set('imwidth', targetWidth)
        return url.toString()
    }
